import { Component, EventEmitter, Inject, Input, Optional, Output } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { TypeaheadService } from '@app/services/typeahead.service';
import { MaterialGradeItem, OrderlineCalcFormService } from '../orderline-calc-form/orderline-calc-form.service';
import { GridRow } from '@app/core/components/grid/grid.types';
import { Subject, takeUntil, groupBy, mergeMap, debounceTime, switchMap, catchError, EMPTY, from } from 'rxjs';

@GridModalComponent('materialitem')
@Component({
    selector: 'tt-material-item',
    templateUrl: './material-item.component.html',
    styleUrls: ['./material-item.component.css'],
})
export class MaterialItemComponent {
    @Input()
    public params?: GetMaterialItemParams;

    public get?: MaterialItemGet;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public saving = false;

    public materialGrades: MaterialGradeItem[] = [];

    private destroy$ = new Subject<void>();

    private change$ = new Subject<ChangeMaterialItemParams>();

    constructor(
        private datatask: DataTaskService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        private orderlineCalc: OrderlineCalcFormService,
        @Optional() @Inject(TT_STATE_DIALOG) private stateContext: StateDialogContext,
    ) {
        this.initFromDialog(stateContext);

        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.changesMaterialItem(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateMaterialItemFromResponse });
    }

    private onUpdateMaterialItemFromResponse = (response: Partial<MaterialItemGet>) => {
        if (!this.get) return;

        for (let k in response) {
            let key = k as keyof MaterialItemGet;

            this.get[key] = response[key] as any;
        }
    };

    public async onMaterialGradeSelected(event?: MaterialGradeItem) {
        if (!this.get) return;

        this.get.materialgrade_keyno = event?.materialgrade_keyno ?? '0';
        this.get.materialgrade_name = event?.materialgrade_name ?? '';

        this.onChange('materialgrade_keyno');
    }

    public async openMaterialGrades() {
        if (!this.get) return;

        const response: GridRow | undefined = await this.modal.openStateDialog({ state: 'materialgrades' });

        if (!!response && 'materialgrade_keyno' in response && 'materialgrade_name' in response) {
            this.onMaterialGradeSelected(response as MaterialGradeItem);
        }
    }

    public searchProducts = () => {
        return this.typeahead.lookUpProducts({ prod_name: this.get?.prod_name ?? '' });
    };

    public onProductSelected(event?: Item) {
        if (!this.get) return;

        this.get.prod_id = !!event ? '' + event.item_id : '';
        this.get.prod_name = event?.item_name ?? '';

        this.onChange('prod_id');
    }

    public searchSuppliers = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.supplier_name ?? '', custtype: 'S' });
    };

    public onSupplierSelected(event?: Item) {
        if (!this.get) return;

        this.get.supplier_no = !!event ? '' + event.item_id : '0';
        this.get.supplier_name = event?.item_name ?? '';

        this.onChange('supplier_no');
    }

    public searchCharge = (): Promise<Item[]> => {
        return this.datatask.Post(3939, { searchtext: this.get?.charge_id ?? '' });
    };

    public onChargeSelected(event?: Item) {
        if (!this.get) return;

        this.get.charge_keyno = !!event ? '' + event.item_id : '0';
        this.get.charge_id = event?.item_name ?? '';

        this.onChange('charge_keyno');
    }

    public onChange(key: keyof MaterialItemGet) {
        if (!this.get) return;

        this.change$.next({ ...this.get, colname_changed: key });
    }

    public async save() {
        if (!this.get) return;

        this.saving = true;

        try {
            const { errorcode, errormessage } = await this.saveMaterialItem(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.next({ errorcode, errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getMaterialItem(this.params);
        }
    }

    private async loadMaterialGrades() {
        this.materialGrades = await this.orderlineCalc.searchMaterialGrades({ searchtext: '' });
    }

    /**
     * Retrieves the material item based on the stockbatch keyno.
     *
     * @datatask 3956
     * @datataskName sp_ws_p2_w_materialitem_get
     * @param params the params used for retrieving the material item.
     * @returns promise containing the material item.
     */
    private async getMaterialItem(params: GetMaterialItemParams): Promise<MaterialItemGet> {
        return (await this.datatask.Post(3956, params))[0];
    }

    private async saveMaterialItem(params: MaterialItemGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3957, params))[0];
    }

    private async changesMaterialItem(params: ChangeMaterialItemParams): Promise<Partial<MaterialItemGet>> {
        return (await this.datatask.Post(3958, params))[0];
    }

    private async init() {
        this.loadMaterialGrades();
        await this.loadGet();
    }

    private async initFromDialog(context?: StateDialogContext) {
        if (!!context) {
            context.setSize({ width: 'min(95vw, 110rem)' });
            this.params = context.params as unknown as GetMaterialItemParams;
            await this.init();
            context.setHeading(this.get?.webpage_heading ?? '');
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => context.close(value) });
        }
    }
}

export interface GetMaterialItemParams {
    stockbatch_keyno: string;
    stockbatch_keyno_copyfrom: string;
}

export interface MaterialItemGet {
    stockbatch_keyno: string;
    suppliers_batchno: string;
    stock_datetime: string;
    materialgrade_keyno: string;
    materialgrade_name: string;
    prod_id: string;
    prod_name: string;
    quantity_stock: string | number;
    unit_name: string;
    price_locval: string | number;
    amount_locval: string | number;
    nbr_of_items: string | number;
    thickness_mm: string | number;
    length_mm: string | number;
    height_mm: string | number;
    width_mm: string | number;
    outer_diameter_mm: string | number;
    supplier_no: string;
    supplier_name: string;
    charge_id: string;
    charge_keyno: string;
    description: string;
    webpage_heading: string;
}

export interface ChangeMaterialItemParams extends MaterialItemGet {
    colname_changed: keyof MaterialItemGet;
}
